/**
 * Who sells TwoCoins, in one place for the site, the app and the server.
 *
 * Anything a lawyer, a registrar, a consumer authority or an email law cares
 * about lives here rather than being typed into each page and template, so
 * there is exactly one line to change when an address or a phone arrives.
 * The legal pages, the plan screen and every email read these fields and
 * show a line only once it is real.
 */
export const COMPANY = {
  /**
   * The name that answers for this product in law.
   *
   * This does NOT have to be a registered company. If you operate as an
   * individual, put your own full legal name here — a sole trader is a
   * perfectly valid data controller, and both app stores issue individual
   * developer accounts. "BuildPassing" on its own is a trading name, which is
   * fine on the site but is not what a store or a regulator will match against.
   *
   * Before submitting to either app store this must be the exact name on the
   * developer account. See docs/legal.html.
   */
  legalName: 'BuildPassing',

  /** The name shown in ordinary copy — the footer, the emails, the marketing. */
  tradeName: 'BuildPassing',

  /**
   * Postal address of whoever is responsible for the data.
   *
   * Deliberately EMPTY until there is a real one. The legal pages check for
   * that and fall back to the email contact rather than printing a placeholder
   * — a made-up address in a privacy notice is worse than no address, because
   * it looks like a considered answer.
   *
   * When you do need one:
   *   - Mexican consumer law (LFPC art. 76 Bis III) requires it before an
   *     online sale — so before the first paid subscription in Mexico.
   *   - Mexican data protection law expects the responsable's domicilio in the
   *     aviso de privacidad.
   *   - US law (CAN-SPAM) requires it in any marketing email; a PO box or a
   *     registered virtual mailbox is enough there.
   *   - Both app stores require an address on the developer account, and for
   *     EU distribution they publish it on the store listing under the Digital
   *     Services Act trader rules.
   * Neither requires a company — a personal or fiscal address is enough. See
   * docs/legal.html for the options.
   */
  address: '',

  /**
   * A phone number for complaints and questions, in international format.
   *
   * Mexican consumer law (LFPC art. 76 Bis III) asks an online seller to give
   * its physical address, its phone numbers and its other contact routes
   * BEFORE the sale. Empty until there is a real number — a WhatsApp Business
   * line is fine — and shown beside the address wherever that appears.
   */
  phone: '',

  /** Where the company is established. Used for the governing-law clause. */
  country: 'Mexico',

  domain: 'buildpassing.com',
  url: 'https://buildpassing.com',
  email: 'hola@buildpassing.com',
  privacyEmail: 'hola@buildpassing.com',
  supportEmail: 'hola@buildpassing.com',
} as const;

/** True once a real postal address has been filled in above. */
export const hasPostalAddress = (): boolean => COMPANY.address.trim().length > 0;

/** True once a real phone number has been filled in above. */
export const hasPhone = (): boolean => COMPANY.phone.trim().length > 0;
